import { type QuestionnaireItem, type QuestionnaireResponseItemAnswer } from "fhir/r3"
import { useContext } from "react"
import { HIDE_ON_VALUE_DEFINITION_URL, SUMMARY_TITLE_DEFINITION_URL } from "../../constants/customExtensionUrls"
import { QuestionnaireContext } from "../../context/QuestionnaireContext"
import { dateDisplay, dateTimeDisplay, timeDisplay } from "../../fhirUtils/dateTimeDisplayUtils"
import { getFlattenedAnswerValues, getResponseForQuestion } from "../../fhirUtils/questionnaireUtils"
import { enableWhenConditionsMet } from "./enableWhenConditionsMet"

interface Props {
  question: QuestionnaireItem
  topLevel: boolean
}

export function ShowAnswer({ question, topLevel }: Props): JSX.Element | null {
  const questionnaireContext = useContext(QuestionnaireContext)
  if (!questionnaireContext) {
    throw new Error("ShowAnswer must be used within a QuestionnaireProvider")
  }

  const { responses } = questionnaireContext

  if (question.type === "display") {
    return null
  }

  const questionIsInUse =
    question.enableWhen == null ? true : enableWhenConditionsMet(question.enableWhen ?? [], responses)

  if (!questionIsInUse) return null

  const flattenedValues = getFlattenedAnswerValues(responses)

  const shouldHide = question.extension?.some(
    (ex) => ex.url === HIDE_ON_VALUE_DEFINITION_URL && flattenedValues.includes(ex.valueString),
  )

  if (shouldHide) return null

  const answerDisplay = (): string => {
    const answerDetail: QuestionnaireResponseItemAnswer[] = getResponseForQuestion(question, responses)?.answer ?? []

    if (answerDetail.length === 0) return "N/A"
    switch (question.type) {
      case "string":
      case "text":
        return answerDetail[0]?.valueString ?? "N/A"
      case "boolean":
        return (answerDetail[0]?.valueBoolean ?? false) ? "Yes" : "No"
      case "choice":
      case "open-choice":
        return answerDetail.map((details) => details.valueString).join(", ")
      case "date":
        return answerDetail[0]?.valueDate != null ? dateDisplay(answerDetail[0]?.valueDate) : "N/A"
      case "dateTime":
        return answerDetail[0]?.valueDateTime != null ? dateTimeDisplay(answerDetail[0]?.valueDateTime) : "N/A"
      case "time":
        return answerDetail[0]?.valueTime != null ? timeDisplay(answerDetail[0]?.valueTime) : "N/A"
      default:
        return "N/A"
    }
  }

  const questionText =
    question.extension?.find((ext) => ext.url === SUMMARY_TITLE_DEFINITION_URL)?.valueMarkdown ?? question.text

  return (
    <>
      {topLevel ? <h2>{questionText}</h2> : <h3>{questionText}</h3>}
      {question.type === "group" ? (
        <>
          {(question.item ?? []).map((childQuestion) => (
            <ShowAnswer question={childQuestion} topLevel={false} key={childQuestion.linkId} />
          ))}
        </>
      ) : (
        <p>{answerDisplay()}</p>
      )}
    </>
  )
}
